import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 161511 $
//  Purpose   : search components
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Input } from 'semantic-ui-react';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { get, size, isDigit, strip, dict } from 'ldash';
import { useSelector, useActions, useState, useRef, useDebounce, useEffect, usePhysicalNode, usePrevious, useResettableState } from 'uix/hooks';
import { KeyboardShortCut } from 'uix/components';
import { useTreeNode } from 'uix/db/hooks';
import { TreeNode } from 'uix/types/components';
// ---Local imports
import styles from './styles.css';
// ---Exports
export const QuickSearchInput = ({ fluid = false, width })=>{
    let req_node = usePhysicalNode();
    let i18n = useSelector(Sel.i18n);
    let inputDomNode = useRef();
    let { query, classname: reqClassname, tabname } = useSelector(Sel.request);
    let prevTabname = usePrevious(tabname);
    let leave_search = prevTabname == 'search' && tabname !== 'search';
    let [searchTerm, setSearchTerm] = useResettableState('', [
        leave_search
    ]);
    useEffect(()=>{
        if (reqClassname == 'tree' && tabname == 'search') {
            setSearchTerm(query.query);
        }
    }, [
        query.query
    ]);
    let otid_candidate = isDigit(strip(searchTerm)) ? parseInt(searchTerm) : null;
    if (otid_candidate > 2147483647) {
        otid_candidate = null;
    }
    // XXX: seems not to work if trailing != true, why?
    let [b_otid_candidate] = useDebounce(otid_candidate, 300, {
        trailing: true
    });
    let node = useTreeNode(b_otid_candidate);
    let [otid_jump, set_otid_jump] = useState(null);
    let go = useActions(Actions.go);
    let runSearch = ()=>{
        // control via checked dropdown
        // - VLIDs
        // - Identifier
        // - Titles/Mods
        // - Tree-Captions
        // - Names
        // - Places
        // - Fulltext
        if (otid_candidate) {
            set_otid_jump(otid_candidate);
        } else if (size(searchTerm) > 2) {
            let classname = reqClassname === 'proto' ? 'proto' : 'tree';
            go({
                classname,
                tabname: 'search',
                id: null,
                query: {
                    query: searchTerm
                }
            });
        }
    };
    // node info comes later, so we need to dance with otid_jump
    useEffect(()=>{
        if (otid_jump && node?.otid == otid_jump && node.datatype != -1) {
            let params = {
                classname: reqClassname,
                id: node.otid,
                odid: node.odid
            };
            // assume current class/tab is still valid if target node has the same datatype
            if (node.datatype !== req_node.datatype) {
                params = {
                    ...params,
                    classname: 'tree',
                    tabname: 'id'
                };
            }
            go(params);
            set_otid_jump(null);
        }
    }, [
        node,
        otid_jump
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(KeyboardShortCut, {
                name: "Ctrl-F3",
                onTrigger: (e)=>inputDomNode.current.focus()
            }),
            /*#__PURE__*/ _jsx(Input, {
                size: "mini",
                fluid: fluid,
                width: width,
                value: searchTerm,
                onChange: (event, data)=>setSearchTerm(data.value),
                onKeyDown: (event)=>event.key == 'Enter' ? runSearch() : null,
                placeholder: i18n.doSearch,
                className: styles.quickSearchInput,
                ref: inputDomNode,
                title: "Ctrl+F3",
                name: "quickSearch"
            })
        ]
    });
};
export const ParentAxis = ({ otid, node, parents })=>{
    let parentsByOtid = dict(parents.map((p)=>[
            p.otid,
            p
        ]));
    let pnodes = [];
    let pnode = parentsByOtid[node.otparent];
    while(pnode){
        pnodes = [
            pnode,
            ...pnodes
        ];
        if (pnode.datatype == 1) {
            break;
        }
        pnode = parentsByOtid[pnode.otparent];
    }
    let style = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: '70px',
        marginRight: '5px'
    };
    return /*#__PURE__*/ _jsx("div", {
        style: {
            display: 'flex'
        },
        children: pnodes.map((n)=>/*#__PURE__*/ _jsx(TreeNode, {
                node: n,
                style: style,
                as: "div"
            }, n.otid))
    });
};
